/**
 * Voice rate cards + rates + tenant assignments.
 * Platform-admin only — backend gates each route behind RequirePlatform.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';
import { getAccessToken } from '@/lib/auth';

export interface VoiceRateCard {
  id: number;
  name: string;
  description?: string;
  default_trunk_id: number;
  default_trunk_name: string;
  is_active: boolean;
}

export interface VoiceRate {
  id: number;
  rate_card_id: number;
  country_code: string;
  prefix: string;
  per_minute_cost: number;
  per_minute_sell_price: number;
  /** Minimum billed duration in seconds — the "X" in an X/Y billing model
   *  (e.g. 12 in 12/6). Equals billing_increment_sec for a plain N/N rate. */
  min_duration_sec: number;
  billing_increment_sec: number;
  trunk_id?: number | null;
  trunk_name?: string;
}

export interface VoiceRateCardAssignment {
  id: number;
  tenant_id: number;
  rate_card_id: number;
  is_primary: boolean;
  card_name: string;
  default_trunk_name: string;
}

export interface VoiceRateCardInput {
  name: string;
  description?: string;
  default_trunk_id: number;
  is_active?: boolean;
}

export interface VoiceRateInput {
  country_code: string;
  prefix: string;
  per_minute_cost: number;
  per_minute_sell_price: number;
  /** Minimum billed seconds ("X" in X/Y). Send 0 (or omit) to default it
   *  to billing_increment_sec server-side — a plain N/N rate. */
  min_duration_sec: number;
  billing_increment_sec: number;
  trunk_id?: number | null;
}

const CARDS_KEY = ['voice', 'rate-cards'] as const;
const cardKey = (id: number) => ['voice', 'rate-cards', id] as const;
const ratesKey = (cardID: number) => ['voice', 'rate-cards', cardID, 'rates'] as const;
const tenantCardsKey = (tenantID: number) => ['voice', 'tenants', tenantID, 'rate-cards'] as const;

interface ListResponse<T> {
  items: T[];
}

export function useVoiceRateCards() {
  return useQuery({
    queryKey: CARDS_KEY,
    queryFn: async () => {
      const res = await api.get<ListResponse<VoiceRateCard>>('/v1/voice/rate-cards');
      return res.items;
    },
    refetchOnWindowFocus: false,
  });
}

export function useVoiceRateCard(id: number | null) {
  return useQuery({
    queryKey: id ? cardKey(id) : ['voice', 'rate-cards', 'noop'],
    queryFn: () => api.get<VoiceRateCard>(`/v1/voice/rate-cards/${id}`),
    enabled: !!id,
    refetchOnWindowFocus: false,
  });
}

export function useCreateVoiceRateCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: VoiceRateCardInput) =>
      api.post<VoiceRateCard>('/v1/voice/rate-cards', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CARDS_KEY });
    },
  });
}

export function useUpdateVoiceRateCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: VoiceRateCardInput }) =>
      api.put<VoiceRateCard>(`/v1/voice/rate-cards/${id}`, input),
    onSuccess: (_, { id }) => {
      void qc.invalidateQueries({ queryKey: CARDS_KEY });
      void qc.invalidateQueries({ queryKey: cardKey(id) });
    },
  });
}

export function useDeleteVoiceRateCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/v1/voice/rate-cards/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CARDS_KEY });
    },
  });
}

export interface AllVoiceRate extends VoiceRate {
  card_name: string;
}

export interface AllVoiceRatesParams {
  rateCardId?: number | null;
  prefix?: string;
  limit?: number;
  offset?: number;
}

export interface PagedVoiceRates {
  items: AllVoiceRate[];
  total: number;
  limit: number;
  offset: number;
}

/** Platform-wide rates, server-side filtered + paginated. The CONVO card
 *  alone has 200k+ prefixes, so the page always scopes by card and/or
 *  prefix search and pages through results — never the whole table. */
export function useAllVoiceRates(params: AllVoiceRatesParams = {}) {
  const { rateCardId, prefix = '', limit = 100, offset = 0 } = params;
  return useQuery({
    queryKey: ['voice', 'all-rates', rateCardId ?? null, prefix, limit, offset],
    queryFn: async () => {
      const q = new URLSearchParams();
      if (rateCardId) q.set('rate_card_id', String(rateCardId));
      if (prefix) q.set('prefix', prefix);
      q.set('limit', String(limit));
      q.set('offset', String(offset));
      return api.get<PagedVoiceRates>(`/v1/voice/rates?${q.toString()}`);
    },
    refetchOnWindowFocus: false,
  });
}

export function useVoiceRates(cardID: number | null) {
  return useQuery({
    queryKey: cardID ? ratesKey(cardID) : ['voice', 'rates', 'noop'],
    queryFn: async () => {
      const res = await api.get<ListResponse<VoiceRate>>(`/v1/voice/rate-cards/${cardID}/rates`);
      return res.items;
    },
    enabled: !!cardID,
    refetchOnWindowFocus: false,
  });
}

export function useCreateVoiceRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ cardID, input }: { cardID: number; input: VoiceRateInput }) =>
      api.post<{ id: number }>(`/v1/voice/rate-cards/${cardID}/rates`, input),
    onSuccess: (_, { cardID }) => {
      void qc.invalidateQueries({ queryKey: ratesKey(cardID) });
    },
  });
}

export function useUpdateVoiceRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      cardID,
      rateID,
      input,
    }: {
      cardID: number;
      rateID: number;
      input: VoiceRateInput;
    }) => api.put<void>(`/v1/voice/rate-cards/${cardID}/rates/${rateID}`, input),
    onSuccess: (_, { cardID }) => {
      void qc.invalidateQueries({ queryKey: ratesKey(cardID) });
    },
  });
}

export interface VoiceRateImportRowError {
  row: number;
  message: string;
}

export interface VoiceRateImportResult {
  total_rows: number;
  inserted: number;
  updated: number;
  errors: VoiceRateImportRowError[];
}

export function useImportVoiceRates() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ cardID, csv }: { cardID: number; csv: string }) => {
      // The backend reads the raw CSV body. Bypass api.post's JSON
      // wrapping by going through fetch directly with text/csv.
      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080';
      const token = getAccessToken();
      const res = await fetch(`${baseUrl}/v1/voice/rate-cards/${cardID}/rates/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'text/csv',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: csv,
      });
      if (!res.ok) {
        const text = await res.text();
        try {
          const parsed = JSON.parse(text);
          throw new Error(parsed.message ?? `HTTP ${res.status}`);
        } catch {
          throw new Error(`HTTP ${res.status}`);
        }
      }
      return (await res.json()) as VoiceRateImportResult;
    },
    onSuccess: (_, { cardID }) => {
      void qc.invalidateQueries({ queryKey: ratesKey(cardID) });
    },
  });
}

export function useDeleteVoiceRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ cardID, rateID }: { cardID: number; rateID: number }) =>
      api.delete<void>(`/v1/voice/rate-cards/${cardID}/rates/${rateID}`),
    onSuccess: (_, { cardID }) => {
      void qc.invalidateQueries({ queryKey: ratesKey(cardID) });
    },
  });
}

export function useTenantVoiceRateCards(tenantID: number | null) {
  return useQuery({
    queryKey: tenantID ? tenantCardsKey(tenantID) : ['voice', 'tenants', 'noop'],
    queryFn: async () => {
      const res = await api.get<ListResponse<VoiceRateCardAssignment>>(
        `/v1/voice/tenants/${tenantID}/rate-cards`,
      );
      return res.items;
    },
    enabled: !!tenantID,
    refetchOnWindowFocus: false,
  });
}

export function useAssignVoiceRateCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      tenantID,
      rateCardID,
      isPrimary,
    }: {
      tenantID: number;
      rateCardID: number;
      isPrimary: boolean;
    }) =>
      api.post<void>(`/v1/voice/tenants/${tenantID}/rate-cards`, {
        rate_card_id: rateCardID,
        is_primary: isPrimary,
      }),
    onSuccess: (_, { tenantID }) => {
      void qc.invalidateQueries({ queryKey: tenantCardsKey(tenantID) });
    },
  });
}

export function useUnassignVoiceRateCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantID, rateCardID }: { tenantID: number; rateCardID: number }) =>
      api.delete<void>(`/v1/voice/tenants/${tenantID}/rate-cards/${rateCardID}`),
    onSuccess: (_, { tenantID }) => {
      void qc.invalidateQueries({ queryKey: tenantCardsKey(tenantID) });
    },
  });
}
